import type { ProjectTileRoccoFragment } from 'generated/graphql';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
/**
 * Maps a project fragment to a tile view model for display purposes.
 *
 * @param {ProjectTileRoccoFragment} item - The project fragment to be mapped. This includes details such as the project's ID, title, link, and associated images.
 * @param {'featured' | 'default'} - Determines which image to display. 'featured' uses a high-resolution image if available, otherwise defaults to the standard image.
 * @param {DisplayMode} - Specifies the display mode, affecting which image resolution is selected.
 * @returns {ProjectTileViewModel} The mapped project tile view model, including project ID, title, link, images, professional details, and award status.
 */
export const mapProjectToTileViewModel = (
    item: ProjectTileRoccoFragment,
    {
        imageToDisplay = 'default',
        appDisplayMode = 'desktop',
    }: {
        imageToDisplay?: 'featured' | 'default';
        appDisplayMode?: DisplayMode;
    } = {}
): ProjectTileViewModel => {
    const defaultImage =
        appDisplayMode === 'desktop' &&
        item.LibraryThumb?.ThumbImageFile?.RecommendedSlider.Desktop
            ? item.LibraryThumb?.ThumbImageFile?.RecommendedSlider.Desktop
            : item.LibraryThumb?.ThumbImageFile?.RecommendedSlider.Mobile || '';
    const highResImage =
        appDisplayMode === 'desktop'
            ? item.Thumbnail?.desktop
            : item.Thumbnail?.mobile;
    const coverSrc =
        imageToDisplay === 'featured'
            ? highResImage || defaultImage
            : defaultImage;

    return {
        id: item.ID,
        title: item.Title,
        link: item.Link,
        images: [
            {
                src: coverSrc,
                alt: item.Title,
                multiplied: item.LibraryThumb?.IsMultiplied,
            },
        ],
        professional: {
            name: item.Professional?.Title || '',
            id: item.Professional?.ID || 0,
            link: item.Professional?.Link || '',
        },
        video: [],
        hasAwards: !!item.Awards?.edges?.length,
        badge: item.Awards?.edges?.length > 0 ? 'Award winning project' : null,
    };
};
